import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, IconButton, Tooltip, InputLabel, Collapse, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { 
  Grass, 
  Agriculture, 
  Nature, 
  LocalFlorist, 
  Spa, 
  Park, 
  FilterVintage, 
  Grain, 
  Yard, 
  Search,
  Store,
  LocationOn,
  Close,
  Upload,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PrescriptionUpload = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)',
  border: '1px dashed #1e88e5',
}));

const CropCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for crop stores
const DefaultAgricultureLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Bangladesh crops with hierarchical structure
const bangladeshCrops = [
  {
    category: { bangla: "শস্য", english: "Grains" },
    icon: <Grain />,
    items: [
      { bangla: "ধান", english: "Rice", icon: <Grain /> },
      { bangla: "গম", english: "Wheat", icon: <Grain /> },
      { bangla: "ভুট্টা", english: "Maize", icon: <Grain /> },
      { bangla: "যব", english: "Barley", icon: <Grain /> },
      { bangla: "বাজরা", english: "Millet", icon: <Grain /> },
      { bangla: "জোয়ার", english: "Sorghum", icon: <Grain /> }
    ]
  },
  {
    category: { bangla: "ডাল", english: "Pulses" },
    icon: <Grain />,
    items: [
      { bangla: "মসুর ডাল", english: "Lentil", icon: <Grain /> },
      { bangla: "মুগ ডাল", english: "Mung Bean", icon: <Grain /> },
      { bangla: "মাষকলাই ডাল", english: "Black Gram", icon: <Grain /> },
      { bangla: "ছোলা", english: "Chickpeas", icon: <Grain /> },
      { bangla: "খেসারী ডাল", english: "Grass Pea", icon: <Grain /> },
      { bangla: "মটর ডাল", english: "Peas", icon: <Grain /> }
    ]
  },
  {
    category: { bangla: "তেলবীজ", english: "Oilseeds" },
    icon: <LocalFlorist />,
    items: [
      { bangla: "সরিষা", english: "Mustard", icon: <LocalFlorist /> },
      { bangla: "তিল", english: "Sesame", icon: <LocalFlorist /> },
      { bangla: "চীনাবাদাম", english: "Groundnut", icon: <LocalFlorist /> },
      { bangla: "সয়াবিন", english: "Soybean", icon: <LocalFlorist /> },
      { bangla: "সূর্যমুখী", english: "Sunflower", icon: <LocalFlorist /> }
    ]
  },
  {
    category: { bangla: "শাকসবজি", english: "Vegetables" },
    icon: <Grass />,
    items: [
      { bangla: "আলু", english: "Potato", icon: <Grass /> },
      { bangla: "বেগুন", english: "Brinjal", icon: <Grass /> },
      { bangla: "টমেটো", english: "Tomato", icon: <Grass /> },
      { bangla: "কুমড়া", english: "Pumpkin", icon: <Grass /> },
      { bangla: "পালং শাক", english: "Spinach", icon: <Grass /> },
      { bangla: "মূলা", english: "Radish", icon: <Grass /> },
      { bangla: "শশা", english: "Cucumber", icon: <Grass /> },
      { bangla: "লাউ", english: "Bottle Gourd", icon: <Grass /> },
      { bangla: "করলা", english: "Bitter Gourd", icon: <Grass /> }
    ]
  },
  {
    category: { bangla: "ফলমূল", english: "Fruits" },
    icon: <Nature />,
    items: [
      { bangla: "আম", english: "Mango", icon: <Nature /> },
      { bangla: "কলা", english: "Banana", icon: <Nature /> },
      { bangla: "পেঁপে", english: "Papaya", icon: <Nature /> },
      { bangla: "আনারস", english: "Pineapple", icon: <Nature /> },
      { bangla: "পেয়ারা", english: "Guava", icon: <Nature /> },
      { bangla: "লিচু", english: "Litchi", icon: <Nature /> },
      { bangla: "কাঁঠাল", english: "Jackfruit", icon: <Nature /> },
      { bangla: "তরমুজ", english: "Watermelon", icon: <Nature /> },
      { bangla: "নারকেল", english: "Coconut", icon: <Nature /> }
    ]
  },
  {
    category: { bangla: "অর্থকরী ফসল", english: "Cash Crops" },
    icon: <Agriculture />,
    items: [
      { bangla: "পাট", english: "Jute", icon: <Agriculture /> },
      { bangla: "আখ", english: "Sugarcane", icon: <Agriculture /> },
      { bangla: "তামাক", english: "Tobacco", icon: <Agriculture /> },
      { bangla: "চা", english: "Tea", icon: <Agriculture /> }
    ]
  },
  {
    category: { bangla: "মসলা", english: "Spices" },
    icon: <Spa />,
    items: [
      { bangla: "মরিচ", english: "Chili", icon: <Spa /> },
      { bangla: "রসুন", english: "Garlic", icon: <Spa /> },
      { bangla: "পেঁয়াজ", english: "Onion", icon: <Spa /> },
      { bangla: "আদা", english: "Ginger", icon: <Spa /> },
      { bangla: "হলুদ", english: "Turmeric", icon: <Spa /> },
      { bangla: "ধনিয়া", english: "Coriander", icon: <Spa /> },
      { bangla: "মেথি", english: "Fenugreek", icon: <Spa /> },
      { bangla: "কালোজিরা", english: "Black Cumin", icon: <Spa /> }
    ]
  }
];

const stores = [
  {
    name: 'সবুজ কৃষি ভান্ডার',
    englishName: 'Green Agriculture Store',
    logo: '/images/logos/agriculture1.png',
    description: 'প্রাকৃতিক ও জৈব ফসলের জন্য বিখ্যাত'
  },
  {
    name: 'বাংলা কৃষক কেন্দ্র',
    englishName: 'Bangla Farmer Center',
    logo: '/images/logos/agriculture2.png',
    description: 'কৃষকদের প্রথম পছন্দ'
  },
  {
    name: 'ফসল ঘর',
    englishName: 'Crop House',
    logo: '/images/logos/agriculture3.png',
    description: 'তাজা ও স্বাস্থ্যকর ফসলের সমাহার'
  },
  {
    name: 'গ্রাম কৃষি',
    englishName: 'Village Agriculture',
    logo: '/images/logos/agriculture4.png',
    description: 'সরাসরি কৃষক থেকে ভোক্তার কাছে'
  },
  {
    name: 'প্রাকৃতিক কৃষি বাজার',
    englishName: 'Natural Agriculture Market',
    logo: '/images/logos/agriculture5.png',
    description: 'রাসায়নিক মুক্ত ফসলের বিশেষজ্ঞ'
  },
  {
    name: 'কৃষি সেবা',
    englishName: 'Agriculture Service',
    logo: '/images/logos/agriculture6.png',
    description: 'সারাদেশের কৃষি পণ্য সরবরাহকারী'
  },
  {
    name: 'হরিৎ কৃষি',
    englishName: 'Green Agriculture',
    logo: '/images/logos/agriculture7.png',
    description: 'পরিবেশবান্ধব কৃষি পণ্য'
  },
  {
    name: 'কৃষক বাজার',
    englishName: 'Farmer Market',
    logo: '/images/logos/agriculture8.png',
    description: 'কৃষকের নিজস্ব বিক্রয় কেন্দ্র'
  },
];

const crops = [
  {
    id: 1,
    name: 'বাসমতি ধান',
    englishName: 'Basmati Rice',
    category: 'ধান',
    store: 'সবুজ কৃষি ভান্ডার',
    price: '৳৮৫.০০',
    image: '/images/crops/rice1.png',
    description: 'উচ্চ মানের বাসমতি ধান। সুগন্ধযুক্ত এবং দীর্ঘ দানার জন্য বিখ্যাত। বিরিয়ানি ও পোলাও তৈরির জন্য আদর্শ।',
    supplier: 'সবুজ কৃষি ভান্ডার'
  },
  {
    id: 2,
    name: 'দেশি আম',
    englishName: 'Local Mango',
    category: 'আম',
    store: 'বাংলা কৃষক কেন্দ্র',
    price: '৳১২০.০০',
    image: '/images/crops/mango1.png',
    description: 'রসালো ও মিষ্টি দেশি আম। প্রাকৃতিকভাবে পাকানো এবং কোনো রাসায়নিক ব্যবহার করা হয়নি।',
    supplier: 'বাংলা কৃষক কেন্দ্র'
  },
  {
    id: 3,
    name: 'জৈব আলু',
    englishName: 'Organic Potato',
    category: 'আলু',
    store: 'প্রাকৃতিক কৃষি বাজার',
    price: '৳৪৫.০০',
    image: '/images/crops/potato1.png',
    description: 'রাসায়নিক সার ছাড়া উৎপাদিত জৈব আলু। পুষ্টিগুণ ভরপুর এবং স্বাস্থ্যের জন্য উপকারী।',
    supplier: 'প্রাকৃতিক কৃষি বাজার'
  },
  {
    id: 4,
    name: 'মসুর ডাল',
    englishName: 'Red Lentil',
    category: 'মসুর ডাল',
    store: 'ফসল ঘর',
    price: '৯৫.০০',
    image: '/images/crops/lentil1.png',
    description: 'উন্নত জাতের মসুর ডাল। প্রোটিনে ভরপুর এবং রান্নায় সহজে সিদ্ধ হয়।',
    supplier: 'ফসল ঘর'
  },
  {
    id: 5,
    name: 'সরিষার তেল',
    englishName: 'Mustard Oil',
    category: 'সরিষা',
    store: 'গ্রাম কৃষি',
    price: '৳১৮০.০০',
    image: '/images/crops/mustard1.png',
    description: 'খাঁটি সরিষার তেল। কোল্ড প্রেসড পদ্ধতিতে তৈরি এবং কোনো রাসায়নিক প্রক্রিয়া ব্যবহার করা হয়নি।',
    supplier: 'গ্রাম কৃষি'
  },
  {
    id: 6,
    name: 'তাজা টমেটো',
    englishName: 'Fresh Tomato',
    category: 'টমেটো',
    store: 'কৃষি সেবা',
    price: '৳৬৫.০০',
    image: '/images/crops/tomato1.png',
    description: 'রসালো ও টাজা টমেটো। ভিটামিন সি এবং লাইকোপিনে ভরপুর। সালাদ ও তরকারির জন্য উপযুক্ত।',
    supplier: 'কৃষি সেবা'
  },
  {
    id: 7,
    name: 'পালং শাক',
    englishName: 'Spinach',
    category: 'পালং শাক',
    store: 'হরিৎ কৃষি',
    price: '৳২৫.০০',
    image: '/images/crops/spinach1.png',
    description: 'তাজা পালং শাক। আয়রন ও ভিটামিনে ভরপুর। স্বাস্থ্যকর ও পুষ্টিগুণ সমৃদ্ধ।',
    supplier: 'হরিৎ কৃষি'
  },
  {
    id: 8,
    name: 'কাঁচা মরিচ',
    englishName: 'Green Chili',
    category: 'মরিচ',
    store: 'কৃষক বাজার',
    price: '৳৮০.০০',
    image: '/images/crops/chili1.png',
    description: 'তাজা ও ঝাল কাঁচা মরিচ। রান্নায় স্বাদ বাড়ানোর জন্য অপরিহার্য উপাদান।',
    supplier: 'কৃষক বাজার'
  },
  {
    id: 9,
    name: 'দেশি কলা',
    englishName: 'Local Banana',
    category: 'কলা',
    store: 'সবুজ কৃষি ভান্ডার',
    price: '৫৫.০০',
    image: '/images/crops/banana1.png',
    description: 'মিষ্টি ও পুষ্টিকর দেশি কলা। পটাশিয়াম ও ভিটামিনে ভরপুর। সহজপাচ্য ও শক্তিদায়ক।',
    supplier: 'সবুজ কৃষি ভান্ডার'
  },
  {
    id: 10,
    name: 'হলুদ গুঁড়া',
    englishName: 'Turmeric Powder',
    category: 'হলুদ',
    store: 'বাংলা কৃষক কেন্দ্র',
    price: '৩২০.০০',
    image: '/images/crops/turmeric1.png',
    description: 'খাঁটি হলুদ গুঁড়া। অ্যান্টি-ইনফ্লামেটরি গুণসম্পন্ন এবং ঔষধি গুণাগুণে ভরপুর।',
    supplier: 'বাংলা কৃষক কেন্দ্র'
  },
  {
    id: 11,
    name: 'তাজা পেঁপে',
    englishName: 'Fresh Papaya',
    category: 'পেঁপে',
    store: 'ফসল ঘর',
    price: '৪০.০০',
    image: '/images/crops/papaya1.png',
    description: 'মিষ্টি ও রসালো পেঁপে। পাচক এনজাইম ও ভিটামিন এ সমৃদ্ধ। হজমে সহায়ক।',
    supplier: 'ফসল ঘর'
  },
  {
    id: 12,
    name: 'কাঁচা পেঁয়াজ',
    englishName: 'Raw Onion',
    category: 'পেঁয়াজ',
    store: 'প্রাকৃতিক কৃষি বাজার',
    price: '৭৫.০০',
    image: '/images/crops/onion1.png',
    description: 'তাজা কাঁচা পেঁয়াজ। রান্নার অপরিহার্য উপাদান। অ্যান্টিঅক্সিডেন্ট ও সালফার যৌগে ভরপুর।',
    supplier: 'প্রাকৃতিক কৃষি বাজার'
  },
  {
    id: 13,
    name: 'চীনাবাদাম',
    englishName: 'Groundnut',
    category: 'চীনাবাদাম',
    store: 'গ্রাম কৃষি',
    price: '১৮০.০০',
    image: '/images/crops/groundnut1.png',
    description: 'তাজা চীনাবাদাম। প্রোটিন ও স্বাস্থ্যকর চর্বিতে ভরপুর। নাস্তা ও তেল উৎপাদনের জন্য ব্যবহৃত।',
    supplier: 'গ্রাম কৃষি'
  },
  {
    id: 14,
    name: 'মুগ ডাল',
    englishName: 'Mung Bean',
    category: 'মুগ ডাল',
    store: 'কৃষি সেবা',
    price: '১১০.০০',
    image: '/images/crops/mung1.png',
    description: 'উচ্চ মানের মুগ ডাল। সহজপাচ্য ও প্রোটিনে ভরপুর। খিচুড়ি ও ডাল রান্নার জন্য আদর্শ।',
    supplier: 'কৃষি সেবা'
  },
  {
    id: 15,
    name: 'তাজা শশা',
    englishName: 'Fresh Cucumber',
    category: 'শশা',
    store: 'হরিৎ কৃষি',
    price: '৩৫.০০',
    image: '/images/crops/cucumber1.png',
    description: 'তাজা ও কুমকুমে শশা। পানির পরিমাণ বেশি এবং গরমে শরীর ঠান্ডা রাখে। সালাদের জন্য উপযুক্ত।',
    supplier: 'হরিৎ কৃষি'
  },
  {
    id: 16,
    name: 'পাকা পেয়ারা',
    englishName: 'Ripe Guava',
    category: 'পেয়ারা',
    store: 'কৃষক বাজার',
    price: '৬০.০০',
    image: '/images/crops/guava1.png',
    description: 'মিষ্টি পাকা পেয়ারা। ভিটামিন সি এর চমৎকার উৎস। রোগ প্রতিরোধ ক্ষমতা বৃদ্ধি করে।',
    supplier: 'কৃষক বাজার'
  },
  {
    id: 17,
    name: 'গোল্ডেন কর্ন',
    englishName: 'Golden Corn',
    category: 'ভুট্টা',
    store: 'সবুজ কৃষি ভান্ডার',
    price: '৫০.০০',
    image: '/images/crops/corn1.png',
    description: 'মিষ্টি গোল্ডেন কর্ন। ফাইবার ও কার্বোহাইড্রেটে ভরপুর। সিদ্ধ করে খাওয়া যায় বা পপকর্ন তৈরি করা যায়।',
    supplier: 'সবুজ কৃষি ভান্ডার'
  },
  {
    id: 18,
    name: 'তিল বীজ',
    englishName: 'Sesame Seeds',
    category: 'তিল',
    store: 'বাংলা কৃষক কেন্দ্র',
    price: '২২০.০০',
    image: '/images/crops/sesame1.png',
    description: 'খাঁটি তিল বীজ। ক্যালসিয়াম ও স্বাস্থ্যকর চর্বিতে ভরপুর। তেল উৎপাদন ও খাবারে ব্যবহারের জন্য।',
    supplier: 'বাংলা কৃষক কেন্দ্র'
  },
  {
    id: 19,
    name: 'তাজা বেগুন',
    englishName: 'Fresh Brinjal',
    category: 'বেগুন',
    store: 'ফসল ঘর',
    price: '৪২.০০',
    image: '/images/crops/brinjal1.png',
    description: 'তাজা ও কোমল বেগুন। ভাজি ও তরকারি রান্নার জন্য চমৎকার। কম ক্যালোরি ও ফাইবার সমৃদ্ধ।',
    supplier: 'ফসল ঘর'
  },
  {
    id: 20,
    name: 'দেশি রসুন',
    englishName: 'Local Garlic',
    category: 'রসুন',
    store: 'প্রাকৃতিক কৃষি বাজার',
    price: '২৮০.০০',
    image: '/images/crops/garlic1.png',
    description: 'তীব্র সুগন্ধযুক্ত দেশি রসুন। অ্যান্টিবায়োটিক গুণসম্পন্ন এবং রান্নায় স্বাদ বৃদ্ধিকারী।',
    supplier: 'প্রাকৃতিক কৃষি বাজার'
  }
];

// All 64 districts of Bangladesh with both Bangla and English names
const districts = [
  { bn: 'সকল জেলা', en: 'All Districts' },
  { bn: 'ঢাকা', en: 'Dhaka' },
  { bn: 'ফরিদপুর', en: 'Faridpur' },
  { bn: 'গাজীপুর', en: 'Gazipur' },
  { bn: 'গোপালগঞ্জ', en: 'Gopalganj' },
  { bn: 'জামালপুর', en: 'Jamalpur' },
  { bn: 'কিশোরগঞ্জ', en: 'Kishoreganj' },
  { bn: 'মাদারীপুর', en: 'Madaripur' },
  { bn: 'মানিকগঞ্জ', en: 'Manikganj' },
  { bn: 'মুন্সিগঞ্জ', en: 'Munshiganj' },
  { bn: 'ময়মনসিংহ', en: 'Mymensingh' },
  { bn: 'নারায়ণগঞ্জ', en: 'Narayanganj' },
  { bn: 'নরসিংদী', en: 'Narsingdi' },
  { bn: 'নেত্রকোনা', en: 'Netrokona' },
  { bn: 'রাজবাড়ী', en: 'Rajbari' },
  { bn: 'শরীয়তপুর', en: 'Shariatpur' },
  { bn: 'শেরপুর', en: 'Sherpur' },
  { bn: 'টাঙ্গাইল', en: 'Tangail' },
  { bn: 'বগুড়া', en: 'Bogura' },
  { bn: 'জয়পুরহাট', en: 'Joypurhat' },
  { bn: 'নওগাঁ', en: 'Naogaon' },
  { bn: 'নাটোর', en: 'Natore' },
  { bn: 'চাঁপাইনবাবগঞ্জ', en: 'Chapainawabganj' },
  { bn: 'পাবনা', en: 'Pabna' },
  { bn: 'রাজশাহী', en: 'Rajshahi' },
  { bn: 'সিরাজগঞ্জ', en: 'Sirajganj' },
  { bn: 'দিনাজপুর', en: 'Dinajpur' },
  { bn: 'গাইবান্ধা', en: 'Gaibandha' },
  { bn: 'কুড়িগ্রাম', en: 'Kurigram' },
  { bn: 'লালমনিরহাট', en: 'Lalmonirhat' },
  { bn: 'নীলফামারী', en: 'Nilphamari' },
  { bn: 'পঞ্চগড়', en: 'Panchagarh' },
  { bn: 'রংপুর', en: 'Rangpur' },
  { bn: 'ঠাকুরগাঁও', en: 'Thakurgaon' },
  { bn: 'বরগুনা', en: 'Barguna' },
  { bn: 'বরিশাল', en: 'Barishal' },
  { bn: 'ভোলা', en: 'Bhola' },
  { bn: 'ঝালকাঠি', en: 'Jhalokati' },
  { bn: 'পটুয়াখালী', en: 'Patuakhali' },
  { bn: 'পিরোজপুর', en: 'Pirojpur' },
  { bn: 'বান্দরবান', en: 'Bandarban' },
  { bn: 'ব্রাহ্মণবাড়িয়া', en: 'Brahmanbaria' },
  { bn: 'চাঁদপুর', en: 'Chandpur' },
  { bn: 'চট্টগ্রাম', en: 'Chattogram' },
  { bn: 'কুমিল্লা', en: 'Cumilla' },
  { bn: 'কক্সবাজার', en: 'Cox\'s Bazar' },
  { bn: 'ফেনী', en: 'Feni' },
  { bn: 'খাগড়াছড়ি', en: 'Khagrachhari' },
  { bn: 'লক্ষ্মীপুর', en: 'Lakshmipur' },
  { bn: 'নোয়াখালী', en: 'Noakhali' },
  { bn: 'রাঙ্গামাটি', en: 'Rangamati' },
  { bn: 'হবিগঞ্জ', en: 'Habiganj' },
  { bn: 'মৌলভীবাজার', en: 'Moulvibazar' },
  { bn: 'সুনামগঞ্জ', en: 'Sunamganj' },
  { bn: 'সিলেট', en: 'Sylhet' },
  { bn: 'বাগেরহাট', en: 'Bagerhat' },
  { bn: 'চুয়াডাঙ্গা', en: 'Chuadanga' },
  { bn: 'যশোর', en: 'Jashore' },
  { bn: 'ঝিনাইদহ', en: 'Jhenaidah' },
  { bn: 'খুলনা', en: 'Khulna' },
  { bn: 'কুষ্টিয়া', en: 'Kushtia' },
  { bn: 'মাগুরা', en: 'Magura' },
  { bn: 'মেহেরপুর', en: 'Meherpur' },
  { bn: 'নড়াইল', en: 'Narail' },
  { bn: 'সাতক্ষীরা', en: 'Satkhira' }
];

// Mock upazillas data with district associations
const upazillasByDistrict = {
  'ঢাকা': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
    { bn: 'মিরপুর', en: 'Mirpur' },
    { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
    { bn: 'গুলশান', en: 'Gulshan' },
    { bn: 'বনানী', en: 'Banani' },
    { bn: 'উত্তরা', en: 'Uttara' },
    { bn: 'রমনা', en: 'Ramna' },
    { bn: 'মতিঝিল', en: 'Motijheel' },
    { bn: 'খিলগাঁও', en: 'Khilgaon' },
    { bn: 'সাভার', en: 'Savar' },
    { bn: 'কেরাণীগঞ্জ', en: 'Keraniganj' },
    { bn: 'ধামরাই', en: 'Dhamrai' },
    { bn: 'দোহার', en: 'Dohar' },
    { bn: 'নবাবগঞ্জ', en: 'Nawabganj' }
  ],
  'চট্টগ্রাম': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'আগ্রাবাদ', en: 'Agrabad' },
    { bn: 'চান্দগাঁও', en: 'Chandgaon' },
    { bn: 'হালিশহর', en: 'Halishahar' },
    { bn: 'পতেঙ্গা', en: 'Patenga' },
    { bn: 'বায়েজিদ', en: 'Bayejid' },
    { bn: 'সীতাকুন্ড', en: 'Sitakunda' },
    { bn: 'মিরসরাই', en: 'Mirsharai' },
    { bn: 'পটিয়া', en: 'Patiya' },
    { bn: 'সন্দ্বীপ', en: 'Sandwip' },
    { bn: 'আনোয়ারা', en: 'Anwara' },
    { bn: 'চন্দনাইশ', en: 'Chandanaish' },
    { bn: 'সাতকানিয়া', en: 'Satkania' },
    { bn: 'লোহাগাড়া', en: 'Lohagara' },
    { bn: 'বাঁশখালী', en: 'Banshkhali' },
    { bn: 'বোয়ালখালী', en: 'Boalkhali' }
  ],
  // Add more districts with their upazillas as needed
};

// Add a default set of upazillas for districts we haven't fully mapped
const defaultUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'সদর উপজেলা', en: 'Sadar Upazila' },
  { bn: 'উপশহর', en: 'Upashahar' },
  { bn: 'পৌরসভা', en: 'Pourashava' }
];

// Default upazillas for "All Districts" option
const allUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
  { bn: 'মিরপুর', en: 'Mirpur' },
  { bn: 'গুলশান', en: 'Gulshan' },
  { bn: 'উত্তরা', en: 'Uttara' },
  { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
  { bn: 'বনানী', en: 'Banani' },
  { bn: 'সিলেট সদর', en: 'Sylhet Sadar' },
  { bn: 'চট্টগ্রাম সদর', en: 'Chattogram Sadar' },
  { bn: 'খুলনা সদর', en: 'Khulna Sadar' },
  { bn: 'রাজশাহী সদর', en: 'Rajshahi Sadar' }
];

// List of bazars in Bangla
const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

const ShoshoBazar = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreTab, setSelectedStoreTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});

  // State for filtered upazillas based on selected district
  const [availableUpazillas, setAvailableUpazillas] = useState(allUpazillas);

  const searchPlaceholders = [
    'ফসলের নাম লিখুন...',
    'কৃষি পণ্য খুঁজুন...',
    'সরবরাহকারীর নাম লিখুন...',
    'ফসলের ধরন লিখুন...',
    'দোকানের নাম লিখুন...'
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = searchPlaceholders[placeholderIndex];
    
    // Handle typing or deleting effect
    const handleTyping = () => {
      // If currently deleting text
      if (isDeleting) {
        // Set text to the substring minus the last character
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50); // Faster speed when deleting
        
        // When text is fully deleted, start typing next placeholder
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % searchPlaceholders.length);
          setTypingSpeed(150); // Normal speed for typing
        }
      } 
      // If currently typing text
      else {
        // Add next character to current text
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150); // Normal speed for typing
        
        // When text is fully typed, pause before deleting
        if (typingText === currentPlaceholder) {
          // Pause at the end of typing before starting to delete
          setTypingSpeed(2000); // Longer pause before deleting
          setIsDeleting(true);
        }
      }
    };
    
    // Set up timer for typing/deleting animation
    const timer = setTimeout(handleTyping, typingSpeed);
    
    // Clean up the timer
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, searchPlaceholders, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    // Toggle expanded state for this category
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleMainCategorySelect = (categoryKey) => {
    if (selectedCategory === categoryKey) {
      setSelectedCategory('');
      setSelectedSubCategory('');
    } else {
      setSelectedCategory(categoryKey);
      setSelectedSubCategory('');
    }
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    setSelectedStoreTab(0); // Reset tab to "All" when selecting a new store
  };

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      // Here you would typically upload the file to your server
      // and process the prescription
    }
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleDistrictChange = (event) => {
    const district = event.target.value;
    setSelectedDistrict(district);
    
    // Reset upazilla when district changes
    setSelectedUpazilla('সকল উপজেলা');
    setUpazillaSearchQuery('');
    
    // Update available upazillas based on selected district
    if (district === 'সকল জেলা') {
      setAvailableUpazillas(allUpazillas);
    } else {
      // Look up the district's upazillas, or use defaults if not specifically mapped
      const districtUpazillas = upazillasByDistrict[district] || defaultUpazillas;
      setAvailableUpazillas(districtUpazillas);
    }
  };

  // Filter districts based on search query
  const filteredDistricts = districts.filter(
    district => district.bn.toLowerCase().includes(districtSearchQuery.toLowerCase())
  );

  // Filter upazillas based on search query
  const filteredUpazillas = availableUpazillas.filter(
    upazilla => upazilla.bn.toLowerCase().includes(upazillaSearchQuery.toLowerCase())
  );

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  // Custom dropdown render props
  const districtMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  const upazillaMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  const filteredCrops = crops.filter((crop) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? crop.category === selectedSubCategory : 
       bangladeshCrops.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === crop.category));
    const matchesStore = !selectedStore || crop.store === selectedStore.name;
    
    // Add store tab filtering
    let matchesStoreTab = true;
    if (selectedStore && selectedStoreTab > 0) {
      const storeCategories = getStoreCategoriesWithCounts();
      const selectedTabCategory = storeCategories[selectedStoreTab]?.name;
      matchesStoreTab = crop.category === selectedTabCategory;
    }
    
    const matchesSearch = !searchQuery || 
      crop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      crop.englishName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesStoreTab && matchesSearch;
  });

  // Get unique categories for the selected store
  const getStoreCategoriesWithCounts = () => {
    if (!selectedStore) return [];
    
    const storeProducts = crops.filter(crop => crop.store === selectedStore.name);
    const categoryMap = new Map();
    
    // Count crops in each category
    storeProducts.forEach(crop => {
      const count = categoryMap.get(crop.category) || 0;
      categoryMap.set(crop.category, count + 1);
    });
    
    // Convert to array and add "All" option
    const categories = [
      { name: 'সকল ফসল', count: storeProducts.length },
      ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
    ];
    
    return categories;
  };

  const handleStoreTabChange = (event, newValue) => {
    setSelectedStoreTab(newValue);
  };

  return (
    <>
      <Header />
      <Box className="crop-bazar-container" sx={{ flexGrow: 1, p: 2 }}>
        {/* New Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/grains.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />

          {/* Crop Upload Button */}
          <Box sx={{ zIndex: 2 }}>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={handlePrescriptionUpload}
              sx={{ display: 'none' }}
              id="quick-crop-upload"
            />
            <label htmlFor="quick-crop-upload">
              <Tooltip title="ফসলের তথ্য আপলোড করুন">
                <Button 
                  variant="contained" 
                  component="span" 
                  startIcon={<Upload />}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #f5f5f5 30%, #ffffff 90%)',
                    color: '#5e35b1',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 20px 0px rgba(0, 0, 0, 0.14)',
                    height: '56px',
                    minWidth: '200px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)',
                      boxShadow: '0 6px 20px 0px rgba(0, 0, 0, 0.18)',
                    }
                  }}
                >
                  ফসলের তথ্য আপলোড
                </Button>
              </Tooltip>
            </label>
          </Box>
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Grass sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    ফসলের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {bangladeshCrops.map((categoryData, index) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    কৃষি দোকান
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore?.name === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            component="img"
                            src={store.logo}
                            alt={store.name}
                            sx={{
                              width: 20,
                              height: 20,
                              objectFit: 'contain',
                              borderRadius: 1,
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              e.target.style.display = 'block';
                              e.target.nextSibling.style.display = 'none';
                            }}
                          />
                          <DefaultAgricultureLogo sx={{ display: 'none' }}>
                            <Nature sx={{ fontSize: 12, color: '#1976d2' }} />
                          </DefaultAgricultureLogo>
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={`${store.englishName} • ${store.description}`}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore?.name === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Store Details Section */}
              {selectedStore && (
                <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #f8f9fa 30%, #e3f2fd 90%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #bbdefb',
                      mr: 3
                    }}>
                      <Agriculture sx={{ fontSize: 30, color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        {selectedStore.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                        {selectedStore.englishName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedStore.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            সেবা এলাকা: সারাদেশ
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Agriculture sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            ফসলের সংখ্যা: {filteredCrops.length}টি
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedStore(null)}
                      startIcon={<Close />}
                      sx={{ ml: 2 }}
                    >
                      বন্ধ করুন
                    </Button>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                    {selectedStore.name} এর ফসলসমূহ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    নিচে {selectedStore.name} এর সকল উপলব্ধ কৃষি পণ্য দেখানো হয়েছে। আপনি চাইলে ক্যাটেগরি অনুযায়ী ফিল্টার করতে পারেন।
                  </Typography>
                  
                  {/* Category Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs 
                      value={selectedStoreTab} 
                      onChange={handleStoreTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 'auto',
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#1976d2',
                        }
                      }}
                    >
                      {getStoreCategoriesWithCounts().map((category, index) => (
                        <Tab 
                          key={index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {category.name}
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: selectedStoreTab === index ? '#1976d2' : '#e0e0e0',
                                color: selectedStoreTab === index ? 'white' : '#666',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}>
                                {category.count}
                              </Box>
                            </Box>
                          }
                          sx={{
                            color: selectedStoreTab === index ? '#1976d2' : 'text.secondary',
                            '&.Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Paper>
              )}
              
              {/* Crop Grid */}
              {filteredCrops.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredCrops.map((crop) => (
                    <Box key={crop.id} sx={{ display: 'flex', width: '100%' }}>
                      <CropCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={crop.image}
                            alt={crop.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', pt: 1 }}
                          />
                          <StyledBadge>{crop.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="div">
                            {crop.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {crop.englishName}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.primary">
                              <strong>সরবরাহকারী:</strong> {crop.supplier}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              <strong>দোকান:</strong> {crop.store}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Typography variant="h6" color="primary">
                              {crop.price}
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                            >
                              কার্টে যোগ করুন
                            </Button>
                          </Box>
                        </CardContent>
                      </CropCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Nature sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন ফসল পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore(null);
                      setSearchQuery('');
                      setSelectedBazar('সকল বাজার');
                      setExpandedCategories({});
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default ShoshoBazar;
